import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Button, Card, Input, useToast } from '@caruso/ui';
import { usersService, User, UserRole, CreateUserDto } from '@/services/users.service';
import { positionsService, Position } from '@/services/positions.service';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  UserCog,
  X,
  Shield,
  Mail,
  Lock,
  ListTodo,
  Badge,
  Calendar as CalendarIcon,
} from 'lucide-react';

export function EmployeesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [formData, setFormData] = useState<CreateUserDto>({
    email: '',
    password: '',
    name: '',
    role: UserRole.USER,
    hasCalendar: false,
  });

  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersService.getAll(),
  });

  const { data: positionsResponse } = useQuery({
    queryKey: ['positions'],
    queryFn: () => positionsService.getAll({ active: true }),
  });

  const positions = positionsResponse?.data || [];

  const createMutation = useMutation({
    mutationFn: (user: CreateUserDto) => usersService.create(user),
    onSuccess: async (newUser) => {
      // Associar cargos selecionados
      for (const positionId of selectedPositions) {
        try {
          await usersService.addPosition(newUser.id, positionId);
        } catch (error) {
          console.error('Erro ao associar cargo:', error);
        }
      }

      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: 'Colaborador criado',
        description: 'O colaborador foi criado com sucesso',
        variant: 'success',
      });
      handleCloseModal();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao criar colaborador',
        description: error.response?.data?.message || 'Erro desconhecido',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      usersService.update(id, data),
    onSuccess: async (_, variables) => {
      const userId = variables.id;

      // Buscar cargos atuais do usuário
      const currentPositions = await usersService.getUserPositions(userId);
      const currentPositionIds = currentPositions.map((p: any) => p.id);

      // Remover cargos que não estão mais selecionados
      for (const positionId of currentPositionIds) {
        if (!selectedPositions.includes(positionId)) {
          try {
            await usersService.removePosition(userId, positionId);
          } catch (error) {
            console.error('Erro ao remover cargo:', error);
          }
        }
      }

      // Adicionar novos cargos selecionados
      for (const positionId of selectedPositions) {
        if (!currentPositionIds.includes(positionId)) {
          try {
            await usersService.addPosition(userId, positionId);
          } catch (error) {
            console.error('Erro ao associar cargo:', error);
          }
        }
      }

      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: 'Colaborador atualizado',
        description: 'O colaborador foi atualizado com sucesso',
        variant: 'success',
      });
      handleCloseModal();
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao atualizar colaborador',
        description: error.response?.data?.message || 'Erro desconhecido',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: 'Colaborador removido',
        description: 'O colaborador foi removido com sucesso',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Erro ao remover colaborador',
        variant: 'destructive',
      });
    },
  });

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Tem certeza que deseja excluir o colaborador ${name}?`))
      return;
    deleteMutation.mutate(id);
  };

  const handleEdit = async (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '', // Não preenche a senha ao editar
      name: user.name,
      role: user.role,
      hasCalendar: user.hasCalendar || false,
    });

    // Carregar cargos do usuário
    try {
      const userPositions = await usersService.getUserPositions(user.id);
      setSelectedPositions(userPositions.map((p: any) => p.id));
    } catch (error) {
      console.error('Erro ao carregar cargos do usuário:', error);
      setSelectedPositions([]);
    }

    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setSelectedPositions([]);
    setFormData({
      email: '',
      password: '',
      name: '',
      role: UserRole.USER,
      hasCalendar: false,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (editingUser) {
      // Remove password do payload se estiver vazio
      const updateData: any = {
        email: formData.email,
        name: formData.name,
        role: formData.role,
        hasCalendar: formData.hasCalendar,
      };
      if (formData.password) {
        updateData.password = formData.password;
      }
      updateMutation.mutate({ id: editingUser.id, data: updateData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const filteredUsers = users?.filter((user) =>
    user.name.toLowerCase().includes(search.toLowerCase()) ||
    user.email.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">
            Colaboradores
          </h1>
          <p className="text-brand-ivory/60 mt-1">
            Gerencie os colaboradores da plataforma Caruso
          </p>
        </div>
        <Button onClick={() => setShowModal(true)} variant="premium">
          <Plus className="w-4 h-4 mr-2" />
          Novo Colaborador
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-brand-ink/50 border-brand-gold/20">
        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold/50" />
            <Input
              placeholder="Buscar por nome ou email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
            />
          </div>
        </div>
      </Card>

      {/* Users List */}
      {isLoading ? (
        <div className="text-center py-12 text-brand-ivory/50">
          Carregando...
        </div>
      ) : (
        <>
          <div className="grid gap-4">
            {filteredUsers?.map((user) => (
              <motion.div
                key={user.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.01 }}
              >
                <Card className="bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all">
                  <div className="p-6 flex items-center justify-between">
                    <div
                      className="flex-1 cursor-pointer"
                      onClick={() => handleEdit(user)}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <UserCog className="w-5 h-5 text-brand-gold" />
                        <h3 className="text-lg font-serif text-brand-gold">
                          {user.name}
                        </h3>
                        {user.role === UserRole.ADMIN && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-brand-gold/20 text-brand-gold border border-brand-gold/30">
                            <Shield className="h-3 w-3" />
                            Admin
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-4 text-sm text-brand-ivory/60">
                        <span className="flex items-center gap-1">
                          <Mail className="h-3.5 w-3.5" />
                          {user.email}
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-brand-ivory/50">
                        Cadastrado em {formatDate(user.createdAt)}
                      </div>
                    </div>
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/app/funcionarios/${user.id}/processos`)}
                        title="Gerenciar processos"
                      >
                        <ListTodo className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(user)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(user.id, user.name)}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {filteredUsers?.length === 0 && (
            <div className="text-center py-12">
              <UserCog className="w-16 h-16 mx-auto text-brand-gold/20 mb-4" />
              <p className="text-brand-ivory/50">
                {search
                  ? 'Nenhum colaborador encontrado'
                  : 'Nenhum colaborador cadastrado ainda'}
              </p>
              {!search && (
                <Button
                  onClick={() => setShowModal(true)}
                  variant="outline"
                  className="mt-4"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar primeiro colaborador
                </Button>
              )}
            </div>
          )}
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-brand-ink border border-brand-gold/20 rounded-lg p-6 max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-serif text-brand-gold">
                {editingUser ? 'Editar Colaborador' : 'Novo Colaborador'}
              </h2>
              <Button variant="ghost" size="icon" onClick={handleCloseModal}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-brand-ivory mb-2">
                  Nome Completo
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Digite o nome completo"
                  required
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-ivory mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold/50" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    placeholder="email@exemplo.com"
                    required
                    className="pl-10 bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-ivory mb-2">
                  Senha {editingUser && '(deixe em branco para manter a atual)'}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold/50" />
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    placeholder="••••••••"
                    required={!editingUser}
                    minLength={6}
                    className="pl-10 bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-ivory mb-2">
                  Permissão
                </label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      role: e.target.value as UserRole,
                    })
                  }
                  className="w-full px-3 py-2 bg-brand-smoke/50 border border-brand-gold/20 rounded-lg text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
                >
                  <option value={UserRole.USER}>Usuário</option>
                  <option value={UserRole.ADMIN}>Administrador</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-ivory mb-2 flex items-center gap-2">
                  <Badge className="w-4 h-4" />
                  Cargos
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto bg-brand-smoke/30 border border-brand-gold/20 rounded-lg p-3">
                  {positions.length === 0 ? (
                    <p className="text-sm text-brand-ivory/50 text-center py-2">
                      Nenhum cargo disponível
                    </p>
                  ) : (
                    positions.map((position) => (
                      <label
                        key={position.id}
                        className="flex items-center gap-2 p-2 hover:bg-brand-gold/10 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedPositions.includes(position.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPositions([...selectedPositions, position.id]);
                            } else {
                              setSelectedPositions(
                                selectedPositions.filter((id) => id !== position.id)
                              );
                            }
                          }}
                          className="w-4 h-4 rounded border-brand-gold/30 bg-brand-smoke/50 text-brand-gold focus:ring-brand-gold/50"
                        />
                        <span className="text-sm text-brand-ivory">{position.name}</span>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-xs text-brand-ivory/50 mt-1">
                  Selecione um ou mais cargos para este colaborador
                </p>
              </div>

              <div className="bg-brand-smoke/30 border border-brand-gold/20 rounded-lg p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.hasCalendar}
                    onChange={(e) =>
                      setFormData({ ...formData, hasCalendar: e.target.checked })
                    }
                    className="w-4 h-4 rounded border-brand-gold/30 bg-brand-smoke/50 text-brand-gold focus:ring-brand-gold/50"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium text-brand-ivory">
                      <CalendarIcon className="w-4 h-4 text-brand-gold" />
                      Habilitar Agenda
                    </div>
                    <p className="text-xs text-brand-ivory/50 mt-1">
                      O colaborador terá acesso a uma agenda pessoal para gerenciar compromissos e eventos
                    </p>
                  </div>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                  className="flex-1"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Salvando...'
                    : editingUser
                    ? 'Atualizar'
                    : 'Criar Colaborador'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
